import { action, observable, runInAction, when } from 'mobx';

import AnalyticsClient from '../clients/AnalyticsClient';
import availableProductsClient from '../clients/AvailableProductsClient';
import identityClient, { JoinableSite, JoinableSitesResponse } from '../clients/identityClient';
import productConfigurationClient from '../clients/ProductConfigurationClient';
import {
  AvailableProduct,
  AvailableProductsResponse,
  AvailableSite,
  IconType,
  LinkConfiguration,
  LinkConfigurationMap,
  ProductConfigurationMap,
  ProductConfigurationResponse,
} from '../clients/sites/model';
import HttpError from '../errors/HttpError';
import {
  getProductConfiguration,
  PRODUCT_KEYS_TO_IDENTITY_APPLICATION,
  ProductAndLinkConfiguration,
  productConfigurationResponseToProductTypeMap,
  ProductKeys,
  SUPPORTED_JOINABLE_SITES,
} from '../model/productType';
import { captureException, captureFatal } from '../sentry';
import { ErrorSource } from '../sentry/types';
import { getHelpfulLinks, StaticLinkConfig } from '../ui/Products/LinkCard/helpfulLinks';

import { ApiStore } from './base/apiStore';
import UserStore from './userStore';

export interface Product {
  cloudId: string;
  count: number;
  icon: IconType;
  productKey: ProductKeys | string;
  productName: JSX.Element | string;
  siteName: string;
  siteRelevance?: number;
  siteSize?: number;
  url: string;
  userAccessLevel?: string;
}

export interface Site<P extends Product = Product> {
  adminAccess: boolean;
  cloudId: string;
  name: string;
  products: P[];
  relevance?: number;
  size?: number;
  url: string;
}

const PRODUCTS_SORTING_USING_ACTIVITIES_THRESHOLD = 6;

export default class ProductStore extends ApiStore {
  @observable availableProducts: Product[] = [];
  @observable availableSites: Site<Product>[] = [];
  @observable isUserAdmin: boolean = false;
  @observable joinableProducts: Product[] = [];
  @observable joinableSites: Site<Product>[] = [];
  @observable linkConfiguration: LinkConfigurationMap = StaticLinkConfig;
  // Default to static link config
  @observable links: LinkConfiguration[] = getHelpfulLinks(StaticLinkConfig);
  @observable productConfiguration: ProductConfigurationMap = {};
  readonly userStore: UserStore;

  private availableProductsResponsePromise?: Promise<AvailableProductsResponse>;
  private joinableSitesResponsePromise?: Promise<JoinableSitesResponse>;
  private productConfigurationResponsePromise?: Promise<ProductConfigurationResponse>;

  constructor(userStore: UserStore) {
    super();
    this.userStore = userStore;
  }

  static fetchAvailableProducts(): Promise<AvailableProductsResponse> {
    return availableProductsClient.getAvailableProducts({ retryMS: [250] });
  }

  static async fetchJoinableSites(): Promise<JoinableSitesResponse> {
    try {
      return await identityClient.getJoinableSites();
    } catch (e) {
      captureException(e);
      return { joinableSites: [] };
    }
  }

  static fetchProductConfiguration(): Promise<ProductConfigurationResponse> {
    return productConfigurationClient.getProductConfiguration({ retryMS: [250] });
  }

  async getAvailableSites(): Promise<AvailableProductsResponse> {
    if (!this.availableProductsResponsePromise) {
      this.availableProductsResponsePromise = ProductStore.fetchAvailableProducts();
    }
    return this.availableProductsResponsePromise;
  }

  async getJoinableSites(): Promise<JoinableSitesResponse> {
    if (!this.joinableSitesResponsePromise) {
      this.joinableSitesResponsePromise = ProductStore.fetchJoinableSites();
    }
    return this.joinableSitesResponsePromise;
  }

  async getProductConfiguration(): Promise<ProductAndLinkConfiguration> {
    if (!this.productConfigurationResponsePromise) {
      this.productConfigurationResponsePromise = ProductStore.fetchProductConfiguration();
    }
    const productConfiguration = await this.productConfigurationResponsePromise;
    return productConfigurationResponseToProductTypeMap(productConfiguration);
  }

  @action
  async getProductsAndJoinableSites(analyticsClient?: AnalyticsClient) {
    try {
      if (!this.isDone) {
        this.setLoadingApiState();
      }

      const availableProductsResponse = await this.getAvailableSites();
      const { products: productConfiguration, links: linkConfiguration } = await this.getProductConfiguration();
      const availableSites = this.mapAvailableSites(availableProductsResponse, productConfiguration);
      const availableProducts = mapProducts(availableSites);

      const joinableSitesResponse = await this.getJoinableSites();
      const joinableSites = mapJoinableSites(joinableSitesResponse.joinableSites, productConfiguration);

      joinableSites.sort((a, b) => (a.name ? a.name.localeCompare(b.name) : 0));
      const joinableProducts = mapProducts(joinableSites);

      const isUserAdmin = availableSites.some(site => site.adminAccess);
      const availableLinks = getHelpfulLinks(linkConfiguration, isUserAdmin);

      await when(() => this.userStore.isDone);
      if (this.userStore.hasFailed && analyticsClient) {
        analyticsClient.trackOperational('userStore', 'failed');
      }

      runInAction(() => {
        this.availableSites = availableSites;
        this.availableProducts = availableProducts;
        this.links = availableLinks;
        this.linkConfiguration = linkConfiguration;
        this.productConfiguration = productConfiguration;

        this.joinableSites = joinableSites;
        this.joinableProducts = joinableProducts;

        this.isUserAdmin = isUserAdmin;
      });
      this.setSuccessApiState();
    } catch (e) {
      // Don't want this error to propagate up
      captureFatal(e, { source: ErrorSource.PRODUCT_STORE });
      if (e instanceof HttpError && e.statusCode >= 400 && e.statusCode < 500) {
        this.setClientErrorApiState(e);
      } else {
        this.setErrorApiState(e);
      }
    }
  }

  mapAvailableSites(
    availableProductsResponse: AvailableProductsResponse,
    productConfiguration: ProductConfigurationMap,
  ): Site<Product>[] {
    const numberOfProducts = availableProductsResponse.sites
      .map(site => site.availableProducts.length)
      .reduce((sum, size) => sum + size, 0);

    return mapAvailableSites(availableProductsResponse.sites, productConfiguration);
  }

  prefetchProductsAndJoinableSites() {
    this.availableProductsResponsePromise = ProductStore.fetchAvailableProducts();
    this.productConfigurationResponsePromise = ProductStore.fetchProductConfiguration();
    this.getJoinableSites();
  }

  @action
  setAvailableSites(availableSites: Site<Product>[]) {
    const availableProducts = mapProducts(availableSites);

    this.availableSites = availableSites;
    this.availableProducts = availableProducts;
  }
}

const mapProducts = <P extends Product>(sites: Site<P>[]): P[] => {
  return sites.reduce<P[]>((acc, site: Site<P>) => acc.concat(site.products), []).sort((a, b) => b.count - a.count);
};

export const mapJoinableSites = (
  joinableSites: JoinableSite[],
  productTypes: ProductConfigurationMap,
): Site<Product>[] => {
  return joinableSites.map<Site<Product>>(joinableSite => {
    const { cloudId, displayName: siteName, products, url, size } = joinableSite;

    const joinableProducts: Product[] = products.reduce<Product[]>((acc, joinableSiteKey) => {
      if (!SUPPORTED_JOINABLE_SITES.includes(joinableSiteKey)) {
        return acc;
      }
      const joinableSiteConfig = Object.entries(productTypes).find(([k, c]) => c.key === joinableSiteKey);

      if (!joinableSiteConfig) {
        return acc;
      }
      const [productKey, type] = joinableSiteConfig;
      return [
        ...acc,
        {
          cloudId: joinableSite.cloudId,
          count: 0,
          icon: type.Icon.white,
          productKey,
          productName: type.label,
          siteName,
          siteRelevance: joinableSite.relevance,
          siteSize: size,

          // XXX: As of now, Jira routing has a bug where deep-linking into Jira, if you don't have permissions,
          // will redirect to a project error page (a dead end) instead of the DES join page (through which we want
          // users to join).
          url,
        },
      ];
    }, []);

    return {
      adminAccess: false,
      cloudId,
      name: siteName,
      products: joinableProducts,
      url,
    };
  });
};

export const getProductKey = (cloudId: string, productType: string): string => {
  const product = PRODUCT_KEYS_TO_IDENTITY_APPLICATION[productType as ProductKeys] || productType.toLowerCase();
  return cloudId + '|' + product;
};

export const getProductSiteUrl = (
  product: Pick<AvailableProduct, 'url' | 'productType'>,
  siteUrl: string,
  productConfigUrl: string,
): string => {
  if (product.url) {
    return product.url;
  }

  return siteUrl + productConfigUrl;
};

export const mapAvailableSites = (sites: AvailableSite[], productTypes: ProductConfigurationMap): Site<Product>[] =>
  sites
    .map<Site<Product>>((site: AvailableSite) => {
      const { adminAccess, cloudId, displayName: siteName, url: siteUrl } = site;

      const products = site.availableProducts.reduce<Product[]>((acc, product: AvailableProduct) => {
        const type = getProductConfiguration(product.productType, productTypes);
        if (type) {
          acc.push({
            cloudId,
            count: 0,
            icon: type.Icon.white,
            productKey: product.productType,
            productName: type.label,
            siteName,
            url: getProductSiteUrl(product, siteUrl, type.href),
          });
        }
        return acc;
      }, []);

      return {
        adminAccess: Boolean(adminAccess),
        cloudId,
        name: siteName,
        products,
        url: siteUrl,
      };
    })
    .sort((a, b) => (a.name ? a.name.localeCompare(b.name) : 0));
